import { IonCol, IonContent, IonRow, IonGrid, IonCard, IonCardContent, IonCardHeader, IonButton, useIonViewDidEnter, IonIcon } from '@ionic/react';
import Header from '../components/Header/Header';
import { onScroll } from '../services/sticky-menu.service';
import Footer from '../components/Footer';
import CartBody from '../components/Shop/CartBody';
import CartSubTotal from '../components/Shop/CartSubTotal';
import { useAppContext } from '../services/app-context.service';
import { authModalRef } from '../components/Auth/AuthModal';
import { documentBaseTitle, appSubDir } from '../configs/app';
import ReactGA from "react-ga4";
import { cartOutline, alertCircleOutline, arrowForwardOutline } from 'ionicons/icons';
import { useHistory } from 'react-router';
import './Cart.scss';

const Cart: React.FC = () => {
    const { user, cart, settings } = useAppContext();
    const history = useHistory();
    const minOrderAmount = settings?.min_order_amount ?? 1000;
    const minOrderItemCount = settings?.min_order_item_count ?? 3;

    useIonViewDidEnter(() => {
        const tabBar = document.querySelector('ion-tab-bar');
        if (tabBar !== null) {
            tabBar.style.display = 'none';
        }
        const title = "Shopping Cart";
        ReactGA.send({ hitType: "pageview", page: window.location.pathname, title });
        document.getElementsByTagName("title")[0].innerHTML = title + " | " + documentBaseTitle;
    });

    const isCartEmpty = cart.itemCount < 1;
    const isMinAmountNotMet = cart.total < minOrderAmount;
    const isMinItemsNotMet = cart.itemCount < minOrderItemCount;
    const areConditionsMet = !isMinAmountNotMet && !isMinItemsNotMet;

    function onCheckout() {
        if (!areConditionsMet) {
            return;
        }
        history.push(appSubDir + "checkout");
    }

    return (
        <IonContent fullscreen scrollEvents={true} onIonScroll={onScroll}>
            <Header />
            <IonRow>
                <IonCol sizeXl='10' offsetXl='1' sizeLg='10' offsetLg='1' className='no-padding bg-content' id="cart-page-container">
                    <div className="cart-banner">
                        <div className='title'>
                            <h1>Shopping Cart</h1>
                            <hr />
                            <p>Review your selected products and proceed to checkout</p>
                        </div>
                    </div>

                    <IonGrid className="cart-grid">
                        {isCartEmpty ? (
                            <IonRow className="ion-justify-content-center">
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonCard className="empty-cart-card ion-text-center">
                                        <IonCardContent className="ion-padding">
                                            <IonIcon icon={cartOutline} className="empty-icon" />
                                            <h2>Your shopping cart is empty</h2>
                                            <p>Please add products to your cart before proceeding.</p>
                                            <IonButton color="primary" routerLink={appSubDir} expand="block" className="action-btn">
                                                Shop Now
                                            </IonButton>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            </IonRow>
                        ) : (
                            <IonRow className="cart-row">
                                {/* Left Side: Cart Items */}
                                <IonCol sizeXs="12" sizeMd="7" className="cart-left-col">
                                    <IonCard className="cart-items-card">
                                        <IonCardHeader>
                                            <h2>Items in Cart</h2>
                                        </IonCardHeader>
                                        <IonCardContent>
                                            <CartBody />
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>

                                {/* Right Side: Cart Summary & Actions */}
                                <IonCol sizeXs="12" sizeMd="5" className="cart-right-col">
                                    <IonCard className="cart-summary-card">
                                        <IonCardHeader>
                                            <h2>Summary</h2>
                                        </IonCardHeader>
                                        <IonCardContent>
                                            <div className="cart-subtotal-section">
                                                <CartSubTotal />
                                            </div>

                                            {!areConditionsMet && (
                                                <IonCard color="danger" className="alert-card ion-no-margin ion-margin-bottom">
                                                    <IonCardContent className="ion-padding">
                                                        <div className="alert-content">
                                                            <IonIcon icon={alertCircleOutline} className="alert-icon" />
                                                            <div>
                                                                <h3>অর্ডার শর্তাবলী পূরণ হয়নি</h3>
                                                                <p>
                                                                    অর্ডার করতে হলে কমপক্ষে <b>{minOrderAmount} /=</b> টাকার অথবা <b>{minOrderItemCount}</b> টি প্রোডাক্ট সিলেক্ট করতে হবে।
                                                                </p>
                                                                <p className="current-status">
                                                                    আপনার বর্তমান অর্ডার: <b>{cart.total} /=</b> (<b>{cart.itemCount}</b> টি প্রোডাক্ট)
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </IonCardContent>
                                                </IonCard>
                                            )}

                                            <div className="cart-actions">
                                                {user.token ? (
                                                    <IonButton 
                                                        onClick={onCheckout} 
                                                        color="primary" 
                                                        expand="block" 
                                                        disabled={!areConditionsMet}
                                                        className="checkout-btn"
                                                    >
                                                        অর্ডার করতে আপনার তথ্য দিন <IonIcon icon={arrowForwardOutline} slot="end" />
                                                    </IonButton>
                                                ) : (
                                                    <IonButton 
                                                        onClick={() => authModalRef.current?.present()} 
                                                        color="success" 
                                                        expand="block"
                                                        className="checkout-btn"
                                                    >
                                                        অর্ডার করতে লগ ইন করুন <IonIcon icon={arrowForwardOutline} slot="end" />
                                                    </IonButton>
                                                )}
                                                <IonButton 
                                                    routerLink={appSubDir} 
                                                    fill="outline" 
                                                    expand="block"
                                                    className="continue-shopping-btn"
                                                >
                                                    Continue Shopping
                                                </IonButton>
                                            </div>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            </IonRow>
                        )}
                    </IonGrid>
                </IonCol>
            </IonRow>
            <Footer />
        </IonContent>
    );
};

export default Cart;
